import React, { KeyboardEvent, MouseEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, MessageCircleIcon, TagIcon } from 'lucide-react';
import { candidateJidsForPhone } from '../../lib/whatsapp/jid';
import type { ClientMedium } from '../types/clients';

type ClientStatus = 'lead' | 'active' | 'past' | 'cold';

interface ClientCardModel {
  id: string;
  name: string;
  phone: string;
  status: ClientStatus;
  goal: string;
  whatsappJid: string | null;
  medium: ClientMedium;
}

interface ClientCardProps {
  client: ClientCardModel;
}

const STATUS_STYLES: Record<ClientStatus, { badge: string; avatar: string; text: string }> = {
  lead: {
    badge: 'bg-amber-100 text-amber-700',
    avatar: 'bg-amber-50 text-amber-600',
    text: 'text-amber-600',
  },
  active: {
    badge: 'bg-green-100 text-green-700',
    avatar: 'bg-green-50 text-green-600',
    text: 'text-green-600',
  },
  past: {
    badge: 'bg-slate-200 text-slate-700',
    avatar: 'bg-slate-100 text-slate-600',
    text: 'text-slate-600',
  },
  cold: {
    badge: 'bg-sky-100 text-sky-700',
    avatar: 'bg-sky-50 text-sky-600',
    text: 'text-sky-600',
  },
};

const STATUS_LABELS: Record<ClientStatus, string> = {
  lead: 'Lead',
  active: 'Active',
  past: 'Inactive',
  cold: 'Cold',
};

const MEDIUM_STYLES: Record<ClientMedium, { badge: string; label: string }> = {
  online: {
    badge: 'bg-blue-100 text-blue-700',
    label: 'Online',
  },
  offline: {
    badge: 'bg-amber-100 text-amber-700',
    label: 'Offline',
  },
};

export function ClientCard({ client }: ClientCardProps) {
  const navigate = useNavigate();

  const status = STATUS_STYLES[client.status] ?? STATUS_STYLES.lead;
  const statusLabel = STATUS_LABELS[client.status] ?? 'Lead';
  const mediumStyle = MEDIUM_STYLES[client.medium] ?? MEDIUM_STYLES.online;
  const initials = useMemo(() => {
    return client.name
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 2) || 'CL';
  }, [client.name]);

  const candidateJids = useMemo(
    () => candidateJidsForPhone(client.phone, client.whatsappJid),
    [client.phone, client.whatsappJid],
  );
  const primaryJid = candidateJids[0] ?? null;

  const handleNavigateToClient = () => {
    navigate(`/client/${client.id}`);
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigateToClient();
    }
  };

  const handleChatClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!primaryJid) {
      navigate('/chats');
      return;
    }
    navigate(`/whatsapp/chat/${encodeURIComponent(primaryJid)}`, {
      state: {
        clientId: client.id,
        clientName: client.name,
        clientPhone: client.phone,
        from: 'clients',
      },
    });
  };

  const handleReminderClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigate(`/client/${client.id}?tab=reminders`);
  };

  const handleTagClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigate(`/client/${client.id}?tab=notes`);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleNavigateToClient}
      onKeyDown={handleCardKeyDown}
      className="group flex cursor-pointer select-none gap-4 rounded-3xl border border-slate-100 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5B8E87] focus-visible:ring-offset-2"
    >
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${status.avatar}`}
      >
        <span>{initials}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="truncate text-base font-semibold text-slate-900">{client.name}</h3>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${status.badge}`}>
                {statusLabel}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${mediumStyle.badge}`}>
                {mediumStyle.label}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500">{client.goal}</p>
          </div>
        </div>

        <div className="mt-1 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleChatClick}
            className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#5B8E87] text-white transition hover:bg-[#4F7A74] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B8E87]"
          >
            <MessageCircleIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleReminderClick}
            className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B8E87]"
          >
            <BellIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleTagClick}
            className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5B8E87]"
          >
            <TagIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
